import React, { useState, useEffect, useRef } from "react";
import { getImageUrl } from "../../../../../services/imageStore";
import GeneralIcon from "../../../../../assets";

export default function VariantModal({ onClose, onAdd, properties = [], specs = [], variant = null }) {
  const [name, setName] = useState(variant?.name || "");
  const [description, setDescription] = useState(variant?.description || "");
  const [price, setPrice] = useState(variant?.price || "0,00");
  const [stock, setStock] = useState(variant?.stock || 0);

  // props: specs = lista de specs do produto
const [selectedSpecs, setSelectedSpecs] = useState(variant?.specs || [...specs]); 


  // propriedades selecionadas pelo usuário
 const [selectedProps, setSelectedProps] = useState(variant?.properties || {});

  // imagens
 const [mainImage, setMainImage] = useState(variant?.mainImage || null);
 const [mainPreviewUrl, setMainPreviewUrl] = useState(variant?.mainImage?.url || null);
  const [galleryImages, setGalleryImages] = useState(variant?.galleryImages || []);

  const mainInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  /* -------------------- uploads -------------------- */
  function handleMainImage(e) {
    const file = e.target.files[0];
    if (!file) return;
    setMainImage({ file, url: URL.createObjectURL(file) });
    setMainPreviewUrl(URL.createObjectURL(file));
  }

  function handleGalleryImages(e) {
    const files = Array.from(e.target.files);
    const mapped = files.map(f => ({
      file: f,
      url: URL.createObjectURL(f),
    }));
    setGalleryImages(prev => [...prev, ...mapped]);
  }

  /* -------------------- seleção de propriedades -------------------- */
  function handleSelectProp(propName, value) {
    setSelectedProps(prev => ({ ...prev, [propName]: value }));
  }

  function removeSpec(index) {
  setSelectedSpecs(prev => prev.filter((_, i) => i !== index));
}

function addSpec(spec) {
  // evita duplicatas
  setSelectedSpecs(prev => {
    if (prev.some(s => s.name === spec.name)) return prev;
    return [...prev, spec];
  });
}


  /* -------------------- salvar variante -------------------- */
  function saveVariant() {
    const normalizedMain = mainImage
      ? { file: mainImage.file || null, url: mainImage.url || null }
      : null;
    const normalizedGallery = Array.isArray(galleryImages)
      ? galleryImages.map(img => ({ file: img.file || null, url: img.url || null }))
      : [];

    onAdd({
      name,
      description,
      price,
      stock,
      properties: selectedProps,
      specs: selectedSpecs,
      mainImage: normalizedMain,
      galleryImages: normalizedGallery,
    });
    onClose();
  }

  /* -------------------- Load variant data when editing -------------------- */
  useEffect(() => {
    let mounted = true;

    async function loadVariantData() {
      if (variant) {
        setName(variant.name || "");
        setDescription(variant.description || "");
        setPrice(variant.price || "0,00");
        setStock(variant.stock || 0);
        setSelectedProps(variant.properties || {});
        setSelectedSpecs(variant.specs || [...specs]);
        setMainImage(variant.mainImage || null);
        
        // Resolve main image preview from key
        if (variant?.mainImage?.key) {
          const url = await getImageUrl(variant.mainImage.key);
          if (mounted) setMainPreviewUrl(url);
        } else if (variant?.mainImage?.url) {
          if (mounted) setMainPreviewUrl(variant.mainImage.url);
        } else {
          if (mounted) setMainPreviewUrl(null);
        }
        
        // Resolve gallery images from keys
        if (Array.isArray(variant.galleryImages)) {
          const resolvedGallery = [];
          for (const img of variant.galleryImages) {
            if (img.key) {
              const url = await getImageUrl(img.key);
              resolvedGallery.push({ key: img.key, url });
            } else if (img.url) {
              resolvedGallery.push(img);
            }
          }
          if (mounted) setGalleryImages(resolvedGallery);
        } else {
          if (mounted) setGalleryImages([]);
        }
      } else {
        // Clear form
        if (mounted) {
          setName("");
          setDescription("");
          setPrice("0,00");
          setStock(0);
          setSelectedProps({});
          setSelectedSpecs([...specs]);
          setMainImage(null);
          setMainPreviewUrl(null);
          setGalleryImages([]);
        }
      }
    }

    loadVariantData();

    return () => {
      mounted = false;
    };
  }, [variant, specs]);


  return (
    <div className="fixed inset-0 bg-black/30 z-50">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="min-h-screen flex justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-lg p-5 z-10 max-h-[85vh] overflow-y-auto shadow-xl">

          <h3 className="text-lg font-semibold mb-4">Adicionar variante</h3>

          {/* ------------------------ GALERIA ------------------------ */}
          <div className="space-y-4">

            {/* principal */}
            <div>
              <label className="text-sm font-medium">Imagem principal*</label>

              <div
                onClick={() => mainInputRef.current.click()}
                className="
                  mt-1 border-2 border-dashed border-purple-400 rounded-xl 
                  py-8 flex flex-col items-center justify-center cursor-pointer
                  hover:bg-purple-50 transition
                "
              >
                {mainPreviewUrl ? (
                  <img
                    src={mainPreviewUrl}
                    className="h-40 w-40 object-cover rounded-lg"
                  />
                ) : (
                  <>
                    <GeneralIcon iconName="camera" className="h-8 w-8 text-purple-600" />
                    <span className="text-purple-600 text-sm mt-2">
                      Adicionar imagem
                    </span>
                  </>
                )}
              </div>

              <input
                ref={mainInputRef}
                type="file"
                accept="image/*"
                onChange={handleMainImage}
                className="hidden"
              />
            </div>

            {/* galeria */}
            <div>
              <label className="text-sm font-medium">Galeria de imagens</label>

              <button
                onClick={() => galleryInputRef.current.click()}
                className="
                  w-full mt-2 bg-purple-600 text-white py-2 rounded-lg
                  font-medium hover:bg-purple-700 transition
                "
              >
                Adicionar imagem
              </button>

              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryImages}
                className="hidden"
              />

              {galleryImages.length > 0 && (
  <div className="flex gap-3 mt-3 overflow-x-auto pb-2">
    {galleryImages.map((img, i) => (
      <div key={i} className="relative flex-shrink-0">
        <img
          src={img.url}
          className="h-24 w-24 rounded-lg object-cover border"
        />

        {/* botão X vermelho */}
        <button
          onClick={() =>
            setGalleryImages(prev =>
              prev.filter((_, idx) => idx !== i)
            )
          }
          className="
            absolute top-1 right-1 bg-white text-red-600 
            w-6 h-6 flex items-center justify-center 
            rounded-full shadow hover:bg-red-100 transition
          "
        >
          ×
        </button>
      </div>
    ))}
  </div>
)}


            </div>
          </div>

          {/* ------------------------ SOBRE ------------------------ */}
          <div className="mt-6">
            <div className="font-semibold text-base mb-2">Sobre</div>

            <label className="text-sm font-medium">Nome da variante*</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1 bg-gray-50"
            />

            <label className="text-sm font-medium mt-3 block">
              Descrição detalhada*
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 mt-1 bg-gray-50"
            />
          </div>

          {/* ------------------------ PROPRIEDADES ------------------------ */}
          <div className="mt-6">
            <div className="font-semibold text-base mb-2">Propriedades</div>

            {properties.length === 0 && (
              <div className="text-sm text-gray-500">
                Nenhuma propriedade definida na mercadoria.
              </div>
            )}

            {properties.map((p, idx) => (
  <div key={idx} className="mb-4">
    <label className="text-sm font-medium">{p.name}</label>
    <select
      value={selectedProps[p.name] ?? ""}
      onChange={e => handleSelectProp(p.name, e.target.value)}
      className="w-full border rounded-lg px-3 py-2 mt-1 bg-gray-50"
    >
      <option value="">Selecione...</option>
      {p.values
        .filter(v => v.trim() !== "")
        .map((v, i) => (
          <option key={i} value={v}>{v}</option>
        ))}
    </select>
  </div>
))}

          </div>

          {/* ------------------------ ESPECIFICAÇÕES ------------------------ */}
          <div className="mt-6">
  <div className="flex items-center justify-between mb-2">
    <div className="font-semibold text-base">Especificações (herdadas)</div>
  </div>

  {/* Lista de specs selecionadas para esta variante */}
  {selectedSpecs.length === 0 && (
    <div className="text-sm text-gray-500 mb-2">Nenhuma especificação selecionada</div>
  )}

  {selectedSpecs.map((sp, idx) => (
    <div key={idx} className="flex gap-2 items-center mb-2 bg-gray-50 p-2 rounded">
      <div className="flex-1">
        <div className="font-medium">{sp.name}</div>
        <div className="text-sm text-gray-600">{sp.value}</div>
      </div>
      <button
        onClick={() => removeSpec(idx)}
        className="text-red-500 text-xs p-1 rounded border"
      >
        <GeneralIcon iconName="lixeira" className="h-4 w-4" />
      </button>
    </div>
  ))}

  {/* Botões para adicionar specs do produto que ainda não estão na variante */}
  <div className="flex flex-wrap gap-2 mt-2">
    {specs
      .filter(s => !selectedSpecs.some(sel => sel.name === s.name))
      .map((s, i) => (
        <button
          key={i}
          onClick={() => addSpec(s)}
          className="text-sm text-white bg-purple-600 px-3 py-1 rounded-md hover:bg-purple-700 transition duration-200"
        >
          + {s.name}
        </button>
      ))}
  </div>
</div>


          {/* ------------------------ VALORES ------------------------ */}
          <div className="mt-6">
            <label className="text-sm font-medium">Preço*</label>
            <input
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />

            <label className="text-sm font-medium mt-3 block">
              Quantidade inicial em estoque*
            </label>
            <input
              type="number"
              value={stock}
              onChange={e => setStock(Number(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
          </div>

          {/* ------------------------ BOTÕES ------------------------ */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onClose}
              className="
                w-1/2 py-2 rounded-lg border 
                text-gray-700 font-medium hover:bg-gray-50 transition
              "
            >
              Cancelar
            </button>

            <button
              onClick={saveVariant}
              className="
                w-1/2 py-2 rounded-lg bg-purple-600 
                text-white font-semibold hover:bg-purple-700 transition
              "
            >
              Adicionar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
